import React, { useState, useEffect, useCallback } from "react";
import {
  Users, Search, Building2, MapPin, Send, Mail, Check, X, Trash2, Share2,
  Copy, ShieldCheck, BarChart3, Sparkles, Gauge, ChevronDown, Handshake, Clock, Gift,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { buildOrgTypeOptions, orgTypeMatches, orgTypeLabel, ORG_TYPE_ALL } from "@/lib/orgTypes";
import { useDemo, DemoHint } from "@/components/demo/DemoUi";
import { isMauritiusDestination, MAURITIUS_DEMO_PARTNERS } from "@/lib/mauritiusDemoJourney";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

const isAdminRole = (role) => ["Owner", "Administrator"].includes(role);

const relBadge = {
  none: null,
  active: { label: "Partenaire actif", cls: "bg-[#DCFCE7] text-[#047857]" },
  pending_sent: { label: "Invitation envoyée", cls: "bg-[#FEF3C7] text-[#B45309]" },
  pending_received: { label: "Vous a invité", cls: "bg-[#E0F2FE] text-[#0369A1]" },
};

const SHARE_CATS = [
  { key: "markets", label: "Vue Markets (agrégée)", icon: BarChart3 },
  { key: "opportunities", label: "Opportunities", icon: Sparkles },
  { key: "dashboard", label: "KPIs Dashboard", icon: Gauge },
];

const Toggle = ({ checked, onChange, disabled, testid }) => (
  <button
    type="button"
    data-testid={testid}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
      checked ? "bg-[#3E7256]" : "bg-slate-300"
    }`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
  </button>
);

const TabButton = ({ id, active, onClick, icon: Icon, label, count }) => (
  <button
    data-testid={`tab-${id}`}
    onClick={onClick}
    className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-semibold transition-colors ${
      active ? "border-[#1E3D2B] text-[#1E3D2B]" : "border-transparent text-slate-400 hover:text-slate-600"
    }`}
  >
    <Icon size={16} /> {label}
    {count > 0 && (
      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${active ? "bg-[#1E3D2B] text-white" : "bg-slate-100 text-slate-500"}`}>{count}</span>
    )}
  </button>
);

const PartnerIdentity = ({ partner }) => {
  const { lang } = useLang();
  if (!partner) return <span className="text-sm text-slate-400">—</span>;
  if (partner.external) {
    return (
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FEF3C7]"><Mail size={16} className="text-[#B45309]" /></div>
        <div>
          <p className="text-sm font-semibold text-[#17201A]">{partner.email}</p>
          <p className="text-xs text-slate-400">Invitation externe · en attente d'inscription</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F1F7F3]"><Building2 size={16} className="text-[#3E7256]" /></div>
      <div>
        <p className="text-sm font-semibold text-[#17201A]">{partner.name}</p>
        <p className="text-xs text-slate-400">{partner.type ? orgTypeLabel(partner.type, lang) : ""}{partner.country ? ` · ${partner.country}` : ""}</p>
      </div>
    </div>
  );
};

export default function Partners() {
  const { activeOrgId, activeMembership } = useAuth();
  const { lang } = useLang();
  const { readOnly, org } = useDemo();
  const admin = isAdminRole(activeMembership?.role);
  const [tab, setTab] = useState("directory");
  const [types, setTypes] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [rel, setRel] = useState({ active: [], pending_sent: [], pending_received: [] });
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("__all__");
  const [inviteEmail, setInviteEmail] = useState("");
  const [lastLink, setLastLink] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [sharedData, setSharedData] = useState({});
  const [referral, setReferral] = useState({ referrals: [], summary: "", config: null });
  const [refEmail, setRefEmail] = useState("");
  const [refLink, setRefLink] = useState("");

  const loadReferrals = useCallback(async () => {
    if (!activeOrgId) return;
    try { const { data } = await api.get(`/organizations/${activeOrgId}/referrals`); setReferral(data); }
    catch { /* noop */ }
  }, [activeOrgId]);
  useEffect(() => { loadReferrals(); }, [loadReferrals]);

  const sendReferral = async (e) => {
    e.preventDefault();
    if (!refEmail.trim()) return;
    try {
      const { data } = await api.post(`/organizations/${activeOrgId}/referrals`, { email: refEmail.trim() });
      setRefLink(data.invite_link || "");
      setRefEmail("");
      toast.success("Parrainage créé (lien de démonstration ci-dessous)");
      await loadReferrals();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };

  useEffect(() => { api.get("/org-types").then(({ data }) => setTypes(data.types || [])).catch(() => {}); }, []);

  const loadRel = useCallback(async () => {
    if (!activeOrgId) return;
    const { data } = await api.get(`/organizations/${activeOrgId}/partners`);
    setRel(data);
  }, [activeOrgId]);

  const loadDirectory = useCallback(async () => {
    if (!activeOrgId) return;
    const params = {};
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get(`/organizations/${activeOrgId}/partners/directory`, { params });
    setDirectory(data.organizations || []);
  }, [activeOrgId, q]);

  useEffect(() => { loadRel(); }, [loadRel]);
  useEffect(() => { const t = setTimeout(loadDirectory, 250); return () => clearTimeout(t); }, [loadDirectory]);

  const refresh = async () => { await Promise.all([loadRel(), loadDirectory()]); };

  // Presentation-only normalization: canonical, deduped filter options + client-side filtering.
  const typeOptions = buildOrgTypeOptions(types, lang);
  const mauritiusDemo = readOnly && isMauritiusDestination(org);
  const demoDirectory = mauritiusDemo && directory.length === 0 ? MAURITIUS_DEMO_PARTNERS : directory;
  const filteredDirectory = demoDirectory
    .filter((o) => !q.trim() || o.name.toLowerCase().includes(q.trim().toLowerCase()))
    .filter((o) => orgTypeMatches(o.type, typeFilter));
  const illustrativeDirectory = demoDirectory === MAURITIUS_DEMO_PARTNERS;

  const inviteOrg = async (orgId) => {
    try {
      await api.post(`/organizations/${activeOrgId}/partners/invite`, { target_org_id: orgId });
      toast.success("Invitation de partenariat envoyée");
      await refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };

  const inviteByEmail = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    try {
      const { data } = await api.post(`/organizations/${activeOrgId}/partners/invite`, { email: inviteEmail.trim() });
      setLastLink(data.invite_link || "");
      setInviteEmail("");
      toast.success("Invitation par e-mail créée (lien de démonstration ci-dessous)");
      await refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };

  const respond = async (pid, action) => {
    try {
      await api.post(`/organizations/${activeOrgId}/partners/${pid}/respond`, { action });
      toast.success(action === "accept" ? "Partenariat accepté" : "Invitation refusée");
      await refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };

  const cancelInvite = async (pid) => {
    try { await api.post(`/organizations/${activeOrgId}/partners/${pid}/cancel`); toast.success("Invitation annulée"); await refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };

  const removePartner = async (pid) => {
    try { await api.delete(`/organizations/${activeOrgId}/partners/${pid}`); toast.success("Partenariat supprimé"); await refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };

  const updateSharing = async (p, key, value) => {
    const next = { ...p.my_sharing, [key]: value };
    setRel((r) => ({ ...r, active: r.active.map((x) => x.id === p.id ? { ...x, my_sharing: next } : x) }));
    try { await api.patch(`/organizations/${activeOrgId}/partners/${p.id}/sharing`, next); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); loadRel(); }
  };

  const viewShared = async (pid) => {
    if (expanded === pid) { setExpanded(null); return; }
    setExpanded(pid);
    try { const { data } = await api.get(`/organizations/${activeOrgId}/partners/${pid}/shared-data`); setSharedData((s) => ({ ...s, [pid]: data })); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };

  return (
    <div className="space-y-6" data-testid="partners-page">
      <div>
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3D2B] to-[#2C5540]"><Users size={18} className="text-[#5C9873]" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-[#17201A]">Partners</h1>
            <p className="text-sm text-[#64748B]">Recherchez, invitez et gérez vos partenaires touristiques — vous contrôlez les données que vous partagez.</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 overflow-x-auto border-b border-slate-200">
        <TabButton id="directory" active={tab === "directory"} onClick={() => setTab("directory")} icon={Search} label="Partner Directory" />
        <TabButton id="invitations" active={tab === "invitations"} onClick={() => setTab("invitations")} icon={Mail} label="Invitations" count={rel.pending_sent.length} />
        <TabButton id="pending" active={tab === "pending"} onClick={() => setTab("pending")} icon={Clock} label="Pending Partners" count={rel.pending_received.length} />
        <TabButton id="active" active={tab === "active"} onClick={() => setTab("active")} icon={Handshake} label="Active Partners" count={rel.active.length} />
        <TabButton id="referral" active={tab === "referral"} onClick={() => setTab("referral")} icon={Gift} label="Referral Program" count={referral.referrals.length} />
      </div>

      {/* DIRECTORY */}
      {tab === "directory" && (
        <div className="space-y-4" data-testid="directory-panel">
          {illustrativeDirectory && (
            <div data-testid="partners-demo-notice" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900">
              <strong>Annuaire simulé.</strong> Organisations fictives et non affiliées, présentées uniquement pour illustrer le parcours partenaire.
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            <div className="relative min-w-[240px] flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une organisation…"
                className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-[#5C9873]" />
            </div>
            <div className="min-w-[200px]">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-10 text-sm" data-testid="directory-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  {typeOptions.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`type-opt-${t.value}`}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {filteredDirectory.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white py-14 text-center" data-testid="directory-empty">
              <Building2 size={32} className="mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-500">Aucune organisation trouvée.</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              {filteredDirectory.map((o) => {
                const badge = relBadge[o.relationship_status];
                return (
                  <div key={o.id} data-testid={`dir-row-${o.id}`} className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-3.5 last:border-0">
                    <PartnerIdentity partner={{ name: o.name, type: o.type, country: o.country }} />
                    <div className="flex items-center gap-3">
                      {badge && <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${badge.cls}`}>{badge.label}</span>}
                      {o.relationship_status === "none" && admin && !readOnly && (
                        <button data-testid={`dir-invite-${o.id}`} onClick={() => inviteOrg(o.id)}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-[#3E7256] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#2F5A43]">
                          <Send size={13} /> Inviter
                        </button>
                      )}
                      {o.relationship_status === "none" && readOnly && (
                        <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-800">Exemple</span>
                      )}
                      {o.relationship_status === "pending_received" && admin && (
                        <button data-testid={`dir-accept-${o.partnership_id}`} onClick={() => respond(o.partnership_id, "accept")}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-[#3E7256] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#2F5A43]">
                          <Check size={13} /> Accepter
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* INVITATIONS */}
      {tab === "invitations" && (
        <div className="space-y-5" data-testid="invitations-panel">
          {admin && !readOnly && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="mb-1 text-sm font-bold text-[#17201A]">Inviter par e-mail</h3>
              <p className="mb-3 text-xs text-slate-500">Pour un partenaire absent de Lodge & Co. Un lien d'invitation (démo) sera généré.</p>
              <form onSubmit={inviteByEmail} className="flex flex-wrap gap-2">
                <input data-testid="invite-email-input" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="partenaire@exemple.com" className="h-10 min-w-[240px] flex-1 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#5C9873]" />
                <button data-testid="invite-email-submit" type="submit" className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2C5540]">
                  <Send size={14} /> Envoyer l'invitation
                </button>
              </form>
              {lastLink && (
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-[#FAFAF6] px-3 py-2" data-testid="invite-link">
                  <span className="truncate text-xs text-slate-500">{lastLink}</span>
                  <button onClick={() => { navigator.clipboard?.writeText(lastLink); toast.success("Lien copié"); }} className="ml-auto text-slate-400 hover:text-[#3E7256]"><Copy size={14} /></button>
                </div>
              )}
            </div>
          )}
          {admin && readOnly && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="invitations-readonly">
              <h3 className="mb-1 text-sm font-bold text-[#17201A]">Inviter par e-mail</h3>
              <button data-testid="invite-email-submit-disabled" disabled
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-400 cursor-not-allowed">
                <Send size={14} /> Envoyer l'invitation
              </button>
              <DemoHint />
            </div>
          )}

          <div>
            <h3 className="mb-3 text-sm font-bold text-[#17201A]">Invitations envoyées</h3>
            {rel.pending_sent.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white py-10 text-center text-sm text-slate-400" data-testid="invitations-empty">Aucune invitation en attente.</div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                {rel.pending_sent.map((p) => (
                  <div key={p.id} data-testid={`sent-invite-${p.id}`} className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-3.5 last:border-0">
                    <PartnerIdentity partner={p.partner} />
                    <div className="flex items-center gap-3">
                      <span className="rounded-full bg-[#FEF3C7] px-2.5 py-1 text-[11px] font-semibold text-[#B45309]">En attente</span>
                      {admin && <button data-testid={`cancel-invite-${p.id}`} onClick={() => cancelInvite(p.id)} className="text-slate-400 hover:text-rose-600"><X size={16} /></button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* PENDING */}
      {tab === "pending" && (
        <div data-testid="pending-panel">
          {rel.pending_received.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white py-14 text-center" data-testid="pending-empty">
              <Clock size={32} className="mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-500">Aucune demande de partenariat en attente.</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              {rel.pending_received.map((p) => (
                <div key={p.id} data-testid={`pending-row-${p.id}`} className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-3.5 last:border-0">
                  <PartnerIdentity partner={p.partner} />
                  {admin ? (
                    <div className="flex items-center gap-2">
                      <button data-testid={`accept-${p.id}`} onClick={() => respond(p.id, "accept")} className="inline-flex items-center gap-1.5 rounded-lg bg-[#3E7256] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#2F5A43]"><Check size={13} /> Accepter</button>
                      <button data-testid={`decline-${p.id}`} onClick={() => respond(p.id, "decline")} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3.5 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"><X size={13} /> Refuser</button>
                    </div>
                  ) : <span className="text-xs text-slate-400">Réservé aux administrateurs</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ACTIVE */}
      {tab === "active" && (
        <div className="space-y-4" data-testid="active-panel">
          {rel.active.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white py-14 text-center" data-testid="active-empty">
              <Handshake size={32} className="mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-500">Aucun partenaire actif pour le moment.</p>
            </div>
          ) : rel.active.map((p) => {
            const sd = sharedData[p.id];
            return (
              <div key={p.id} data-testid={`active-row-${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                  <PartnerIdentity partner={p.partner} />
                  {admin && <button data-testid={`remove-partner-${p.id}`} onClick={() => removePartner(p.id)} className="text-slate-300 hover:text-rose-600"><Trash2 size={16} /></button>}
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* What I share */}
                  <div className="rounded-xl border border-[#E8E5DD] bg-[#FAFAF6] p-4">
                    <div className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#2C5540]"><ShieldCheck size={13} /> Ce que je partage</div>
                    <p className="mb-3 text-[11px] text-slate-400">Aucune donnée privée n'est partagée par défaut.</p>
                    <div className="space-y-2.5">
                      {SHARE_CATS.map((c) => (
                        <div key={c.key} className="flex items-center justify-between">
                          <span className="flex items-center gap-2 text-sm text-[#374151]"><c.icon size={14} className="text-slate-400" /> {c.label}</span>
                          <Toggle testid={`share-toggle-${c.key}-${p.id}`} checked={!!p.my_sharing?.[c.key]} disabled={!admin} onChange={(v) => updateSharing(p, c.key, v)} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* What partner shares */}
                  <div className="rounded-xl border border-[#E8E5DD] bg-white p-4">
                    <div className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#0369A1]"><Share2 size={13} /> Ce que ce partenaire me partage</div>
                    <div className="mb-3 flex flex-wrap gap-1.5">
                      {SHARE_CATS.filter((c) => p.partner_sharing?.[c.key]).map((c) => (
                        <span key={c.key} className="rounded-full bg-[#E0F2FE] px-2 py-0.5 text-[11px] font-semibold text-[#0369A1]">{c.label}</span>
                      ))}
                      {!SHARE_CATS.some((c) => p.partner_sharing?.[c.key]) && <span className="text-[11px] text-slate-400">Ce partenaire ne partage aucune donnée pour l'instant.</span>}
                    </div>
                    {SHARE_CATS.some((c) => p.partner_sharing?.[c.key]) && (
                      <button data-testid={`view-shared-${p.id}`} onClick={() => viewShared(p.id)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3E7256] hover:underline">
                        <ChevronDown size={13} className={expanded === p.id ? "rotate-180 transition-transform" : "transition-transform"} /> {expanded === p.id ? "Masquer" : "Voir les données partagées"}
                      </button>
                    )}
                  </div>
                </div>

                {expanded === p.id && sd && (
                  <div className="mt-4 rounded-xl border border-slate-200 bg-[#FBFCFB] p-4" data-testid={`shared-data-${p.id}`}>
                    {Object.keys(sd.shared || {}).length === 0 ? (
                      <p className="text-sm text-slate-400">Aucune donnée partagée.</p>
                    ) : (
                      <div className="space-y-4 text-sm">
                        {sd.shared.dashboard && (
                          <div>
                            <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">KPIs Dashboard {sd.shared.dashboard.year && `· ${sd.shared.dashboard.year}`}</p>
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                              <div className="rounded-lg bg-white p-2.5"><p className="text-[10px] text-slate-400">Volume</p><p className="font-mono font-semibold text-[#17201A]">{new Intl.NumberFormat("fr-FR").format(sd.shared.dashboard.total_volume)}</p></div>
                              <div className="rounded-lg bg-white p-2.5"><p className="text-[10px] text-slate-400">Revenu</p><p className="font-mono font-semibold text-[#17201A]">{new Intl.NumberFormat("fr-FR").format(sd.shared.dashboard.total_revenue)}</p></div>
                              <div className="rounded-lg bg-white p-2.5"><p className="text-[10px] text-slate-400">Marchés</p><p className="font-mono font-semibold text-[#17201A]">{sd.shared.dashboard.markets_count}</p></div>
                              <div className="rounded-lg bg-white p-2.5"><p className="text-[10px] text-slate-400">Occ. moy.</p><p className="font-mono font-semibold text-[#17201A]">{sd.shared.dashboard.avg_occupancy_pct ?? "—"}{sd.shared.dashboard.avg_occupancy_pct != null ? "%" : ""}</p></div>
                            </div>
                          </div>
                        )}
                        {sd.shared.markets && (
                          <div>
                            <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Top marchés partagés</p>
                            <div className="flex flex-wrap gap-1.5">
                              {sd.shared.markets.top_markets_by_volume.map((m) => (
                                <span key={m.market} className="rounded-md bg-white px-2 py-1 text-xs text-[#374151]">{m.market} · <span className="font-mono">{new Intl.NumberFormat("fr-FR").format(m.volume_total)}</span></span>
                              ))}
                            </div>
                          </div>
                        )}
                        {sd.shared.opportunities && (
                          <div>
                            <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Opportunités partagées</p>
                            <ul className="space-y-1">
                              {sd.shared.opportunities.slice(0, 5).map((o) => (
                                <li key={o.id} className="text-xs text-[#475569]">• <strong>{o.opportunity}</strong> — {o.market} <span className="text-slate-400">({o.potential})</span></li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* REFERRAL PROGRAM */}
      {tab === "referral" && (
        <div className="space-y-5" data-testid="referral-panel">
          <div className="flex flex-col gap-3 rounded-2xl border border-[#BBD8C5] bg-gradient-to-br from-[#F1F7F3] to-white p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#3E7256]"><Gift size={20} className="text-white" /></div>
              <div>
                <h3 className="text-sm font-bold text-[#17201A]">Parrainez un nouvel acteur de votre destination</h3>
                <p className="text-sm text-[#2C5540]" data-testid="referral-reward">Récompense actuelle : <strong>{referral.summary || "—"}</strong></p>
              </div>
            </div>
          </div>

          {admin && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="mb-1 text-sm font-bold text-[#17201A]">Inviter par e-mail</h3>
              <p className="mb-3 text-xs text-slate-500">La personne invitée recevra un lien pour s'inscrire sur Lodge & Co. Votre récompense sera suivie ci-dessous.</p>
              <form onSubmit={sendReferral} className="flex flex-wrap gap-2">
                <input data-testid="referral-email-input" type="email" value={refEmail} onChange={(e) => setRefEmail(e.target.value)}
                  placeholder="nouvel-acteur@exemple.com" className="h-10 min-w-[240px] flex-1 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#5C9873]" />
                <button data-testid="referral-email-submit" type="submit" className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2C5540]">
                  <Send size={14} /> Envoyer le parrainage
                </button>
              </form>
              {refLink && (
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-[#FAFAF6] px-3 py-2" data-testid="referral-link">
                  <span className="truncate text-xs text-slate-500">{refLink}</span>
                  <button onClick={() => { navigator.clipboard?.writeText(refLink); toast.success("Lien copié"); }} className="ml-auto text-slate-400 hover:text-[#3E7256]"><Copy size={14} /></button>
                </div>
              )}
            </div>
          )}

          <div>
            <h3 className="mb-3 text-sm font-bold text-[#17201A]">Mes parrainages</h3>
            {referral.referrals.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white py-10 text-center text-sm text-slate-400" data-testid="referral-empty">Aucun parrainage pour le moment.</div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <div className="overflow-x-auto">
                  <SelectableTable rows={referral.referrals} rowId={(r) => r.id} filename="partners.csv" ariaLabel="Tableau — sélection et défilement">
                    <table className="w-full min-w-[720px]">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"><th className="px-2 py-2"><SelectAllHeaderCheckbox /></th>
                        <th className="px-5 py-3">Invité</th>
                        <th className="px-5 py-3">Date</th>
                        <th className="px-5 py-3">Inscription</th>
                        <th className="px-5 py-3">Abonnement</th>
                        <th className="px-5 py-3">Récompense</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referral.referrals.map((r) => (
                        <tr key={r.id} data-testid={`referral-row-${r.id}`} className="border-b border-slate-100 last:border-0"><td className="px-2 py-2"><RowCheckbox id={r.id} /></td>
                          <td className="px-5 py-3 text-sm font-medium text-[#17201A]">{r.invited_org?.name || r.invited_email}</td>
                          <td className="px-5 py-3 text-xs text-slate-500">{r.invitation_date ? new Date(r.invitation_date).toLocaleDateString("fr-FR") : "—"}</td>
                          <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.registration_status === "registered" ? "bg-[#DCFCE7] text-[#047857]" : "bg-slate-100 text-slate-500"}`}>{r.registration_status === "registered" ? "Inscrit" : "En attente"}</span></td>
                          <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.subscription_status === "subscribed" ? "bg-[#DCFCE7] text-[#047857]" : "bg-slate-100 text-slate-500"}`}>{r.subscription_status === "subscribed" ? "Abonné" : "Aucun"}</span></td>
                          <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${{ paid: "bg-[#DCFCE7] text-[#047857]", approved: "bg-[#E0F2FE] text-[#0369A1]", eligible: "bg-[#FEF3C7] text-[#B45309]", rejected: "bg-rose-100 text-rose-600", pending: "bg-slate-100 text-slate-500" }[r.reward_status]}`}>{r.reward_status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </SelectableTable>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
